import { useEffect, useRef, useState } from "react";
import { app, UserTeamRole } from "@microsoft/teams-js";
import { initAuth, protokoll, abmelden } from "./auth/teamsAuth";
import { graphGet } from "./graph/client";
import { SCOPES } from "./auth/config";
import { adresseFuerBoard, boardAusAdresse } from "./board";
import { ladeBoard, ladeMeineBoards, ladeZeitplan, type Board } from "./zeitleiste/laden";
import { Zeitleiste } from "./zeitleiste/Zeitleiste";
import type { Zeitplan } from "./zeitleiste/planner";

// Rahmen der App: anmelden, Board laden, wach halten. Gezeichnet wird in
// zeitleiste/Zeitleiste.tsx, gerechnet in zeitleiste/kalender.ts.

const AKTUALISIERUNG_MS = 5 * 60 * 1000;

type Ich = { displayName: string; mail: string | null };

type Umfeld = {
  inTeams: boolean;
  ich: Ich;
  teamName?: string;
  rolle?: UserTeamRole;
};

type Lage =
  | { art: "laedt" }
  | { art: "fehler"; text: string }
  // Kein ?plan= in der Adresse: Boards zur Auswahl anbieten.
  | { art: "ohneBoard"; umfeld: Umfeld; boards: Board[] }
  | {
      art: "board";
      umfeld: Umfeld;
      board: Board;
      zeitplan: Zeitplan;
      stand: Date;
      /** Letzte Aktualisierung schlug fehl – die Daten oben sind älter. */
      aktualisierungsFehler?: string;
    };

// ACHTUNG: userRole ist eine ZAHLEN-Aufzählung (Admin=0, User=1, Guest=2),
// kein String. Der in manchen Beispielen gezeigte Vergleich
// `userRole === "Owner"` trifft NIE zu – der Knopf wäre für niemanden da.
// "Admin" heißt hier Team-Besitzer; daran hängt später das Anlegen eines Jahres.
function rolleLesbar(rolle: UserTeamRole): string {
  if (rolle === UserTeamRole.Admin) return "Besitzer";
  if (rolle === UserTeamRole.User) return "Mitglied";
  return "Gast";
}

export function App() {
  const [lage, setLage] = useState<Lage>({ art: "laedt" });
  const [aktualisiertGerade, setAktualisiertGerade] = useState(false);
  // Wird vom Aktualisierungs-Effekt unten gesetzt – solange kein Board da ist, tut er nichts.
  const jetztAktualisieren = useRef<() => void>(() => {});
  // Zweite Sperre gegen den doppelten Effektaufruf im Entwicklungsmodus.
  // Ohne sie wuerde der Ladevorgang zweimal starten und damit auch zweimal
  // ein Anmeldefenster oeffnen.
  const bereitsGeladen = useRef(false);
  const planId = boardAusAdresse();

  useEffect(() => {
    if (bereitsGeladen.current) return;
    bereitsGeladen.current = true;
    (async () => {
      try {
        // Reihenfolge: erst TeamsJS, dann MSAL – siehe teamsAuth.ts
        const { inTeams } = await initAuth();
        const ich = await graphGet<Ich>("/me", SCOPES.ich);

        const umfeld: Umfeld = { inTeams, ich };
        if (inTeams) {
          const kontext = await app.getContext();
          umfeld.teamName = kontext.team?.displayName;
          umfeld.rolle = kontext.team?.userRole;
        }

        if (!planId) {
          setLage({ art: "ohneBoard", umfeld, boards: await ladeMeineBoards() });
          return;
        }

        const [board, zeitplan] = await Promise.all([ladeBoard(planId), ladeZeitplan(planId)]);
        setLage({ art: "board", umfeld, board, zeitplan, stand: new Date() });
      } catch (fehler) {
        setLage({ art: "fehler", text: fehler instanceof Error ? fehler.message : String(fehler) });
      }
    })();
  }, [planId]);

  // Auto-Aktualisierung: alle fünf Minuten und beim Zurückkehren in den Tab.
  // Ein Fehler dabei ist VORÜBERGEHEND: Die alten Daten bleiben stehen, nur
  // ein Hinweis kommt dazu – nie den Stand wegwerfen, nie zur Anmeldung
  // werfen (das tat die alte App bei jedem Netz-Schluckauf).
  //
  // Der Knopf „Jetzt aktualisieren“ ruft DIESELBE Funktion (über den Ref) –
  // mit derselben Sperre gegen doppelte Läufe und derselben Fehlerregel.
  useEffect(() => {
    if (lage.art !== "board" || !planId) return;

    let laeuft = false;
    const aktualisiere = async (erzwungen = false) => {
      // Ein unsichtbarer Tab lädt nicht von selbst – ein Klick ist aber
      // immer sichtbar, deshalb darf der Knopf die Prüfung überspringen.
      if (laeuft || (!erzwungen && document.visibilityState !== "visible")) return;
      laeuft = true;
      setAktualisiertGerade(true);
      try {
        const zeitplan = await ladeZeitplan(planId);
        setLage((alt) =>
          alt.art === "board" ? { ...alt, zeitplan, stand: new Date(), aktualisierungsFehler: undefined } : alt,
        );
      } catch (fehler) {
        const text = fehler instanceof Error ? fehler.message : String(fehler);
        setLage((alt) => (alt.art === "board" ? { ...alt, aktualisierungsFehler: text } : alt));
      } finally {
        laeuft = false;
        setAktualisiertGerade(false);
      }
    };

    jetztAktualisieren.current = () => void aktualisiere(true);
    const takt = setInterval(() => void aktualisiere(), AKTUALISIERUNG_MS);
    const beiSichtbarkeit = () => void aktualisiere();
    document.addEventListener("visibilitychange", beiSichtbarkeit);
    return () => {
      jetztAktualisieren.current = () => {};
      clearInterval(takt);
      document.removeEventListener("visibilitychange", beiSichtbarkeit);
    };
  }, [lage.art, planId]);

  if (lage.art === "laedt") return <main className="huelle">Einen Moment …</main>;

  if (lage.art === "fehler") {
    return (
      <main className="huelle">
        <h1>Das hat nicht geklappt</h1>
        <p className="fehler">{lage.text}</p>
        <h2>Protokoll</h2>
        <pre className="protokoll">{protokoll.join("\n") || "(leer)"}</pre>
      </main>
    );
  }

  if (lage.art === "ohneBoard") {
    return (
      <main className="huelle">
        <h1>Welches Board?</h1>
        <p>
          Diese Adresse nennt noch kein Planner-Board.{" "}
          {lage.umfeld.inTeams
            ? "Entferne den Tab und füge ihn neu hinzu – beim Einrichten wird das Board gewählt und bleibt dann fest."
            : "Zum Ansehen eines wählen:"}
        </p>
        {lage.boards.length ? (
          <ul className="boards">
            {lage.boards.map((b) => (
              <li key={b.id}>
                <a href={adresseFuerBoard(b.id)}>{b.title}</a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="hinweis">Du hast Zugriff auf kein Planner-Board.</p>
        )}
        <Fusszeile umfeld={lage.umfeld} />
      </main>
    );
  }

  return (
    <Zeitleiste
      board={lage.board}
      zeitplan={lage.zeitplan}
      stand={lage.stand}
      aktualisierungsFehler={lage.aktualisierungsFehler}
      onAktualisieren={() => jetztAktualisieren.current()}
      aktualisiertGerade={aktualisiertGerade}
      fusszeile={<Fusszeile umfeld={lage.umfeld} />}
    />
  );
}

// Klein und einklappbar: Wer angemeldet ist, wo die Seite läuft, welche Rolle.
//
// Das beantwortet den Fall, der KEINEN Fehler wirft: falsches Konto, Gastzugang,
// falsches Team – dann sind die Daten leer oder fremd und nichts erklärt warum.
//
// Das Protokoll steht hier bewusst NICHT mehr: Scheitert die Anmeldung, zeigt
// die App ohnehin ihren Fehlerbildschirm, und der bringt es mit. Dieselbe
// Sache an zwei Stellen zu zeigen, läuft früher oder später auseinander.
//
// Der Abmelde-Knopf bleibt: In einem Teams-Kanal-Tab gibt es keine Adresszeile.
// Hängt ein Token im Speicher fest, ist er der einzige Weg heraus, ohne den Tab
// neu einzurichten.
function Fusszeile({ umfeld }: { umfeld: Umfeld }) {
  return (
    <details className="fuss">
      <summary>
        {umfeld.ich.mail ?? umfeld.ich.displayName} · {umfeld.inTeams ? "Teams" : "Browser"}
        {umfeld.teamName ? ` · ${umfeld.teamName}` : ""}
        {umfeld.rolle !== undefined ? ` · ${rolleLesbar(umfeld.rolle)}` : ""}
      </summary>
      <p>
        <button className="knopf" onClick={() => void abmelden()}>
          Abmelden und neu anmelden
        </button>
      </p>
    </details>
  );
}
